import { matchPath } from "react-router-dom";
import type { BreadcrumbItem } from "Helpers/generalBreadcrumbs";

type BreadcrumbRule = {
  path: string;
  crumbs: (params: Record<string, string | undefined>) => BreadcrumbItem[];
};

const customers = (): BreadcrumbItem => ({
  labelKey: "customers",
});

const clientDetailsHref = (p: Record<string, string | undefined>) =>
  p.id && p.accountId && p.invoicingPreference
    ? `/client/${p.id}/account/${p.accountId}/invoicingPreference/${p.invoicingPreference}`
    : undefined;

export const buildClientDetailsHref = clientDetailsHref;

export const buildClientDriversListHref = (
  p: Record<string, string | undefined>,
) =>
  p.id && p.accountId && p.invoicingPreference
    ? `/client/${p.id}/account/${p.accountId}/drivers/invoicingPreference/${p.invoicingPreference}`
    : undefined;

/** Customers sidebar module routes — more specific paths first. */
const CUSTOMERS_BREADCRUMB_RULES: BreadcrumbRule[] = [
  // Clients / customers list
  {
    path: "/clients",
    crumbs: () => [customers(), { labelKey: "customersList" }],
  },
  {
    path: "/client/create",
    crumbs: () => [
      customers(),
      { labelKey: "customersList", href: "/clients" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/client/:id/account/:accountId/update-password/invoicingPreference/:invoicingPreference",
    crumbs: (p) => [
      customers(),
      { labelKey: "customersList", href: "/clients" },
      { labelKey: "Details", href: clientDetailsHref(p) },
      { labelKey: "updatePassword" },
    ],
  },
  {
    path: "/client/:id/account/:accountId/driver/create/invoicingPreference/:invoicingPreference",
    crumbs: (p) => [
      customers(),
      { labelKey: "customersList", href: "/clients" },
      { labelKey: "Details", href: clientDetailsHref(p) },
      {
        labelKey: "drivers",
        href:
          p.id && p.accountId && p.invoicingPreference
            ? `/client/${p.id}/account/${p.accountId}/drivers/invoicingPreference/${p.invoicingPreference}`
            : undefined,
      },
      { labelKey: "create" },
    ],
  },
  {
    path: "/client/:id/account/:accountId/driver/:driverId/invoicingPreference/:invoicingPreference",
    crumbs: (p) => [
      customers(),
      { labelKey: "customersList", href: "/clients" },
      { labelKey: "Details", href: clientDetailsHref(p) },
      {
        labelKey: "drivers",
        href:
          p.id && p.accountId && p.invoicingPreference
            ? `/client/${p.id}/account/${p.accountId}/drivers/invoicingPreference/${p.invoicingPreference}`
            : undefined,
      },
      { labelKey: "Details" },
    ],
  },
  {
    path: "/client/:id/account/:accountId/drivers/invoicingPreference/:invoicingPreference",
    crumbs: (p) => [
      customers(),
      { labelKey: "customersList", href: "/clients" },
      { labelKey: "Details", href: clientDetailsHref(p) },
      { labelKey: "drivers" },
    ],
  },
  {
    path: "/client/:id/account/:accountId/vehicles/invoicingPreference/:invoicingPreference",
    crumbs: (p) => [
      customers(),
      { labelKey: "customersList", href: "/clients" },
      { labelKey: "Details", href: clientDetailsHref(p) },
      { labelKey: "vehicles" },
    ],
  },
  {
    path: "/client/:id/account/:accountId/invoices/invoicingPreference/:invoicingPreference",
    crumbs: (p) => [
      customers(),
      { labelKey: "customersList", href: "/clients" },
      { labelKey: "Details", href: clientDetailsHref(p) },
      { labelKey: "invoices" },
    ],
  },
  {
    path: "/client/:id/account/:accountId/delivery-notes/invoicingPreference/:invoicingPreference",
    crumbs: (p) => [
      customers(),
      { labelKey: "customersList", href: "/clients" },
      { labelKey: "Details", href: clientDetailsHref(p) },
      { labelKey: "deliveryNotes" },
    ],
  },
  {
    path: "/client/:id/account/:accountId/payments/invoicingPreference/:invoicingPreference",
    crumbs: (p) => [
      customers(),
      { labelKey: "customersList", href: "/clients" },
      { labelKey: "Details", href: clientDetailsHref(p) },
      { labelKey: "payments" },
    ],
  },
  {
    path: "/client/:id/account/:accountId/reviews/invoicingPreference/:invoicingPreference",
    crumbs: (p) => [
      customers(),
      { labelKey: "customersList", href: "/clients" },
      { labelKey: "Details", href: clientDetailsHref(p) },
      { labelKey: "reviews" },
    ],
  },
  {
    path: "/client/:id/account/:accountId/integrations/invoicingPreference/:invoicingPreference",
    crumbs: (p) => [
      customers(),
      { labelKey: "customersList", href: "/clients" },
      { labelKey: "Details", href: clientDetailsHref(p) },
      { labelKey: "integrations" },
    ],
  },
  {
    path: "/client/:id/account/:accountId/integration/add/invoicingPreference/:invoicingPreference",
    crumbs: (p) => [
      customers(),
      { labelKey: "customersList", href: "/clients" },
      { labelKey: "Details", href: clientDetailsHref(p) },
      {
        labelKey: "integrations",
        href:
          p.id && p.accountId && p.invoicingPreference
            ? `/client/${p.id}/account/${p.accountId}/integrations/invoicingPreference/${p.invoicingPreference}`
            : undefined,
      },
      { labelKey: "create" },
    ],
  },
  {
    path: "/client/:id/account/:accountId/integration/:integrationId/invoicingPreference/:invoicingPreference",
    crumbs: (p) => [
      customers(),
      { labelKey: "customersList", href: "/clients" },
      { labelKey: "Details", href: clientDetailsHref(p) },
      {
        labelKey: "integrations",
        href:
          p.id && p.accountId && p.invoicingPreference
            ? `/client/${p.id}/account/${p.accountId}/integrations/invoicingPreference/${p.invoicingPreference}`
            : undefined,
      },
      { labelKey: "Details" },
    ],
  },
  {
    path: "/client/:id/account/:accountId/invoicingPreference/:invoicingPreference",
    crumbs: () => [
      customers(),
      { labelKey: "customersList", href: "/clients" },
      { labelKey: "Details" },
    ],
  },

  // Individual customers (accessed from customers list create menu)
  {
    path: "/customers",
    crumbs: () => [customers(), { labelKey: "customersList" }],
  },
  {
    path: "/customer/create",
    crumbs: () => [
      customers(),
      { labelKey: "customersList", href: "/clients" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/customer/:id/update-password",
    crumbs: (p) => [
      customers(),
      { labelKey: "customersList", href: "/clients" },
      {
        labelKey: "Details",
        href: p.id ? `/customer/${p.id}` : undefined,
      },
      { labelKey: "updatePassword" },
    ],
  },
  {
    path: "/customer/:id/vehicles",
    crumbs: (p) => [
      customers(),
      { labelKey: "customersList", href: "/clients" },
      {
        labelKey: "Details",
        href: p.id ? `/customer/${p.id}` : undefined,
      },
      { labelKey: "vehicles" },
    ],
  },
  {
    path: "/customer/:id/invoices",
    crumbs: (p) => [
      customers(),
      { labelKey: "customersList", href: "/clients" },
      {
        labelKey: "Details",
        href: p.id ? `/customer/${p.id}` : undefined,
      },
      { labelKey: "invoices" },
    ],
  },
  {
    path: "/customer/:id/payments",
    crumbs: (p) => [
      customers(),
      { labelKey: "customersList", href: "/clients" },
      {
        labelKey: "Details",
        href: p.id ? `/customer/${p.id}` : undefined,
      },
      { labelKey: "payments" },
    ],
  },
  {
    path: "/customer/:id/reviews",
    crumbs: (p) => [
      customers(),
      { labelKey: "customersList", href: "/clients" },
      {
        labelKey: "Details",
        href: p.id ? `/customer/${p.id}` : undefined,
      },
      { labelKey: "reviews" },
    ],
  },
  {
    path: "/customer/:id",
    crumbs: () => [
      customers(),
      { labelKey: "customersList", href: "/clients" },
      { labelKey: "Details" },
    ],
  },

  // Estimates
  {
    path: "/estimates",
    crumbs: () => [customers(), { labelKey: "estimates" }],
  },
  {
    path: "/estimate/:id",
    crumbs: () => [
      customers(),
      { labelKey: "estimates", href: "/estimates" },
      { labelKey: "Details" },
    ],
  },

  // Job cards
  {
    path: "/job-cards",
    crumbs: () => [customers(), { labelKey: "jobCards" }],
  },
  {
    path: "/job-card/:id",
    crumbs: () => [
      customers(),
      { labelKey: "jobCards", href: "/job-cards" },
      { labelKey: "Details" },
    ],
  },

  // Work orders
  {
    path: "/work-orders",
    crumbs: () => [customers(), { labelKey: "workOrders" }],
  },
  {
    path: "/work-order/:id",
    crumbs: () => [
      customers(),
      { labelKey: "workOrders", href: "/work-orders" },
      { labelKey: "Details" },
    ],
  },

  // Invoices
  {
    path: "/invoices",
    crumbs: () => [customers(), { labelKey: "invoices" }],
  },
  {
    path: "/invoices/:id/template/:templateId",
    crumbs: (p) => [
      customers(),
      { labelKey: "invoices", href: "/invoices" },
      {
        labelKey: "Details",
        href: p.id ? `/invoices/${p.id}` : undefined,
      },
      { labelKey: "businessTemplates" },
    ],
  },
  {
    path: "/invoices/:id",
    crumbs: () => [
      customers(),
      { labelKey: "invoices", href: "/invoices" },
      { labelKey: "Details" },
    ],
  },

  // Inspections
  {
    path: "/inspections",
    crumbs: () => [customers(), { labelKey: "Inspections" }],
  },
  {
    path: "/inspection/:id/template/:templateId",
    crumbs: (p) => [
      customers(),
      { labelKey: "Inspections", href: "/inspections" },
      {
        labelKey: "Details",
        href: p.id ? `/inspection/${p.id}` : undefined,
      },
      { labelKey: "businessTemplates" },
    ],
  },
  {
    path: "/inspection/:id",
    crumbs: () => [
      customers(),
      { labelKey: "Inspections", href: "/inspections" },
      { labelKey: "Details" },
    ],
  },

  // Sales credit notes
  {
    path: "/sales-credit-notes",
    crumbs: () => [customers(), { labelKey: "creditNotes" }],
  },
  {
    path: "/sales-credit-note/create",
    crumbs: () => [
      customers(),
      { labelKey: "creditNotes", href: "/sales-credit-notes" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/sales-credit-note/:id/view",
    crumbs: () => [
      customers(),
      { labelKey: "creditNotes", href: "/sales-credit-notes" },
      { labelKey: "view" },
    ],
  },

  // Payments
  {
    path: "/payments",
    crumbs: () => [customers(), { labelKey: "payments" }],
  },
  {
    path: "/payment/create",
    crumbs: () => [
      customers(),
      { labelKey: "payments", href: "/payments" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/payment/:id",
    crumbs: () => [
      customers(),
      { labelKey: "payments", href: "/payments" },
      { labelKey: "Details" },
    ],
  },

  // Payment modes
  {
    path: "/payment-modes",
    crumbs: () => [customers(), { labelKey: "Payment Modes" }],
  },
  {
    path: "/payment-modes/create",
    crumbs: () => [
      customers(),
      { labelKey: "Payment Modes", href: "/payment-modes" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/payment-modes/:id",
    crumbs: () => [
      customers(),
      { labelKey: "Payment Modes", href: "/payment-modes" },
      { labelKey: "Details" },
    ],
  },
];

export const getCustomersBreadcrumbs = (
  pathname: string,
): BreadcrumbItem[] | null => {
  for (const rule of CUSTOMERS_BREADCRUMB_RULES) {
    const match = matchPath({ path: rule.path, end: true }, pathname);
    if (match) {
      return rule.crumbs(match.params as Record<string, string | undefined>);
    }
  }
  return null;
};
